/** Web Mercator in "world units": the whole world is WU wide at zoom k = 1. */
import { routeLngs, spanLngs } from "../game/geo";

export const WU = 100000;
const R = WU / (2 * Math.PI);

export const X = (lng: number): number => (WU * (lng + 180)) / 360;
export const Y = (lat: number): number => {
  const l = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return WU / 2 - R * Math.log(Math.tan(Math.PI / 4 + l / 2));
};
export const latOfY = (y: number): number => (Math.atan(Math.sinh((WU / 2 - y) / R)) * 180) / Math.PI;
export const lngOfX = (x: number): number => (x / WU) * 360 - 180;
/** Wrap any longitude into [-180, 180). */
export const wrapLng = (lng: number): number => ((lng + 540) % 360) - 180;

/** [lat, lng] pairs of a route → world-unit points, drawn across the same arc the places are framed on (`routeLngs`). */
export function unwrapLine(pts: readonly (readonly [number, number])[]): [number, number][] {
  const lngs = routeLngs(pts.map((p) => p[1]));
  return pts.map((p, i) => [X(lngs[i]!), Y(p[0])]);
}

/** [lat, lng] pairs of a set of places → world-unit points on the shortest arc that holds them all (`spanLngs`), for framing. */
export function spanPoints(pts: readonly (readonly [number, number])[]): [number, number][] {
  const lngs = spanLngs(pts.map((p) => p[1]));
  return pts.map((p, i) => [X(lngs[i]!), Y(p[0])]);
}

/** The point a share `t` (0–1) of the way from a to b on the straight map line `unwrapLine` draws between them. */
export function along(a: { lat: number; lng: number }, b: { lat: number; lng: number }, t: number): { lat: number; lng: number } {
  const [l0, l1] = routeLngs([a.lng, b.lng]) as [number, number];
  const x = X(l0) + (X(l1) - X(l0)) * t;
  const y = Y(a.lat) + (Y(b.lat) - Y(a.lat)) * t;
  return { lat: latOfY(y), lng: wrapLng(lngOfX(x)) };
}
